/*
 * ============================================================
 * argile-feature-tabs.js — onglets « image liée » réutilisables
 * ============================================================
 * Chargé PAR la section (cf. sections/argile-feature-tabs.liquid).
 *
 * Cible `[data-argile-feature-tabs]`. Au clic (ou flèches haut/bas) sur un
 * onglet `[role="tab"]`, sa `data-tab-image` alimente la grande image
 * (`[data-tab-stage]`, cross-fade 2 couches).
 *
 * Chargement des images (simple & sans piège) :
 *   - Deux calques <img> superposés (data-stage-layer), SANS loading="lazy".
 *   - On pose le `src` directement sur le calque ARRIÈRE (invisible, opacity 0),
 *     puis on le révèle à son `onload` → cross-fade. Le calque AVANT reste
 *     affiché pendant le chargement, donc jamais de blanc.
 *   - Au page load, seule l'image de l'onglet actif charge ; les autres se
 *     chargent au moment où l'on bascule dessus (et on ne bascule qu'en vue,
 *     l'autoplay étant mis en pause hors-vue) → pas de rafale de requêtes.
 *
 * Défilement automatique (réglable dans l'éditeur) :
 *   - `setInterval` : change d'onglet toutes les N secondes. Aucun travail par
 *     frame → pas de saccade.
 *   - UN SEUL déclencheur : l'entrée de la section dans le viewport
 *     (IntersectionObserver). Démarre en vue, s'arrête hors vue → pas de cycles
 *     gâchés hors écran. Pause aussi au survol et au focus clavier. L'image
 *     initiale, elle, est posée dès le load (pas de retard d'affichage).
 */
(function () {
  'use strict';

  function initFeatureTabs(root) {
    var scope = root || document;
    var groups = scope.querySelectorAll('[data-argile-feature-tabs]');
    if (!groups.length) return;

    groups.forEach(function (group) {
      if (group.dataset.argileWired) return;
      group.dataset.argileWired = '1';

      var stage = group.querySelector('[data-tab-stage]');
      var layers = stage ? [].slice.call(stage.querySelectorAll('[data-stage-layer]')) : [];
      var frontLayer = 0; // index de la couche actuellement visible
      var tabs = [].slice.call(group.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      var activeIndex = 0;
      var firstPaint = true;

      // Préchargement de TOUTES les images d'onglets dès l'init (4 images
      // légères). Sur une page lourde (beaucoup de requêtes), l'image du
      // changement pouvait sinon être mise en file et n'arriver qu'après de
      // longues secondes → la section semblait « ne pas démarrer ». En les
      // préchargeant, le cross-fade est instantané à chaque changement.
      tabs.forEach(function (tab) {
        var src = tab.getAttribute('data-tab-image');
        if (src) { (new Image()).src = src; }
      });

      function setStage(tab) {
        if (!layers.length) return;
        var src = tab.getAttribute('data-tab-image');
        if (!src) return;
        var back = layers[(frontLayer + 1) % layers.length];
        var front = layers[frontLayer];
        var revealed = false;
        var reveal = function () {
          // Image en cache : `back.complete` est vrai dès l'affectation du `src`
          // ET l'événement `onload` se déclenche quand même → reveal serait
          // appelé deux fois, ce qui ferait avancer `frontLayer` en double et
          // casserait le cross-fade. On garantit une exécution unique.
          if (revealed) return;
          revealed = true;
          back.onload = null;
          if (firstPaint) {
            // Tout 1er affichage : pas de fondu (sinon ~1 s de vide au reload).
            firstPaint = false;
            back.style.transition = 'none';
            back.classList.add('is-ready');
            void back.offsetWidth; // applique opacity:1 immédiatement
            back.style.transition = '';
          } else {
            back.classList.add('is-ready');
          }
          if (front !== back) front.classList.remove('is-ready');
          frontLayer = (frontLayer + 1) % layers.length;
        };
        // Déjà la bonne image sur la couche arrière → on révèle direct.
        if (back.getAttribute('src') === src) { reveal(); return; }
        back.alt = tab.getAttribute('data-tab-alt') || '';
        back.onload = reveal;          // révèle quand l'image est prête
        back.src = src;                // charge sur la couche arrière (invisible)
        if (back.complete && back.naturalWidth) reveal(); // déjà en cache
      }

      function activate(index, focus) {
        activeIndex = index;
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          if (on && focus) tab.focus();
        });
        setStage(tabs[index]);
      }

      // ----- Défilement automatique -----
      var reduceMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var speedSec = parseFloat(group.getAttribute('data-autoplay-speed'));
      if (isNaN(speedSec) || speedSec <= 0) speedSec = 4;
      var durationMs = speedSec * 1000;
      var autoplayOn = group.hasAttribute('data-autoplay') &&
        tabs.length > 1 && !reduceMotion;

      var timer = null;
      var paused = false;  // survol ou focus clavier
      var visible = false; // section dans le viewport (rempli par l'observer)
      function advance() { activate((activeIndex + 1) % tabs.length, false); }
      // Source unique de vérité : l'autoplay ne tourne QUE si activé, visible
      // et non mis en pause. Appelé à chaque changement d'état.
      function tick() {
        var shouldRun = autoplayOn && visible && !paused;
        if (shouldRun && !timer) {
          timer = window.setInterval(advance, durationMs);
        } else if (!shouldRun && timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }
      // Repart de zéro après une interaction (clic / flèche).
      function restart() {
        if (timer) { window.clearInterval(timer); timer = null; }
        tick();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(i, false); restart(); });
      });

      group.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        var current = tabs.indexOf(document.activeElement);
        if (current < 0) return;
        event.preventDefault();
        var next = event.key === 'ArrowDown'
          ? (current + 1) % tabs.length
          : (current - 1 + tabs.length) % tabs.length;
        activate(next, true);
        restart();
      });

      activate(0, false); // pose l'onglet 0 + sa 1re image (instantané)

      if (autoplayOn) {
        // UN SEUL déclencheur : l'entrée dans le viewport. L'autoplay démarre
        // quand la section devient visible et s'arrête quand elle sort (plus de
        // cycles gâchés hors écran). Pause aussi au survol / focus clavier.
        group.addEventListener('mouseenter', function () { paused = true; tick(); });
        group.addEventListener('mouseleave', function () { paused = false; tick(); });
        group.addEventListener('focusin', function () { paused = true; tick(); });
        group.addEventListener('focusout', function () { paused = false; tick(); });

        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            visible = entries[0].isIntersecting;
            tick();
          }, { threshold: 0.25 });
          io.observe(group);
        } else {
          // Vieux navigateur sans observer → on tourne en continu (repli).
          visible = true;
          tick();
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initFeatureTabs(document); });
  } else {
    initFeatureTabs(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initFeatureTabs(event.target);
  });
})();
